import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { PageHeader } from "~/components/page-header";
import { NotificationsCard } from "~/components/notifications-card";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { PasswordInput } from "~/components/ui/password-input";
import { Label } from "~/components/ui/label";
import { api, APIError } from "~/lib/api";
import { useMe } from "~/lib/auth";

export const Route = createFileRoute("/_authed/ops/account")({
  component: OpsAccount,
});

function OpsAccount() {
  const { data: me } = useMe();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [name, setName] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);

  const [password, setPassword] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  useEffect(() => {
    if (!me) return;
    setName(me.name ?? "");
  }, [me]);

  async function saveProfile() {
    setSavingProfile(true);
    try {
      const updated = await api.updateProfile({ name: name.trim() });
      qc.setQueryData(["me"], updated);
      toast.success("Profile saved");
    } catch (e) {
      toast.error(e instanceof APIError ? e.message : "Could not save profile");
    } finally {
      setSavingProfile(false);
    }
  }

  async function savePassword() {
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setSavingPw(true);
    try {
      await api.setPassword(password);
      setPassword("");
      void qc.invalidateQueries({ queryKey: ["me"] });
      toast.success("Password updated");
    } catch (e) {
      toast.error(e instanceof APIError ? e.message : "Could not set password");
    } finally {
      setSavingPw(false);
    }
  }

  async function signOut() {
    try { await api.logout(); } catch { /* ignore */ }
    qc.clear();
    await navigate({ to: "/login" });
  }

  const roleLabel = me?.role === "ops_admin" ? "Administrator" : "Agent";

  return (
    <div className="mx-auto w-full max-w-xl space-y-5">
      <PageHeader eyebrow={roleLabel} title="Account" caption={me?.phone ?? undefined} />

      <Card>
        <CardHeader>
          <CardTitle>Profile</CardTitle>
          <CardDescription>The name shown on tickets and timeline events you act on.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="name">Full name</Label>
            <Input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Erick Kipngeno" />
          </div>
          <Button variant="secondary" className="w-full" loading={savingProfile} onClick={() => void saveProfile()}>
            Save profile
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Password</CardTitle>
          <CardDescription>Optional. You can always sign in with a one-time code.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} placeholder={me?.hasPassword ? "New password" : "Set a password"} />
          <Button variant="secondary" className="w-full" loading={savingPw} onClick={() => void savePassword()}>
            {me?.hasPassword ? "Change password" : "Set password"}
          </Button>
        </CardContent>
      </Card>

      <NotificationsCard />

      <Button variant="destructive" className="w-full" onClick={() => void signOut()}>
        Sign out
      </Button>
    </div>
  );
}
